import { useUser } from '@clerk/react';
import { Link, useLocation } from '@tanstack/react-router';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useId, useRef, useState } from 'react';

import { AppIcon } from '@/components/ui/app-icon';
import { PRIMARY_BUTTON } from '@/components/ui/button';
import { useAppSumoUpgrade } from '@/hooks/use-appsumo-upgrade.hook';
import {
  APPSUMO_UPGRADE_PATH,
  appSumoBannerCopy,
  appSumoUpgradeSearch,
  BANNER_HIDE_LABEL,
  SEE_YOUR_PRICE,
  trackAppSumoUpgrade,
} from '@/lib/appsumo-upgrade';
import { iconArrowRight, iconXmark } from '@/lib/icons';
import { cn } from '@/lib/utils';

/** Per-user, and only for a while: a live price should come back rather
 *  than vanish for good on one stray click. */
const DISMISS_KEY_PREFIX = 'cl_appsumo_upgrade_dismissed_';
export const APPSUMO_BANNER_DISMISS_TTL_MS = 3 * 24 * 60 * 60 * 1000;

function readDismissed(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${userId}`);
    return raw
      ? Date.now() - Number(raw) < APPSUMO_BANNER_DISMISS_TTL_MS
      : false;
  } catch {
    return false;
  }
}

function writeDismissed(userId: string) {
  try {
    localStorage.setItem(`${DISMISS_KEY_PREFIX}${userId}`, String(Date.now()));
  } catch {
    // Storage unavailable (private mode): the bar just comes back next load.
  }
}

/**
 * The AppSumo upgrade campaign's bar, in the AppShell banner slot above the
 * nav and the page on every screen (its height feeds --app-banner-h).
 *
 * Shown to AppSumo-only workspaces while the offer is open. Until the final
 * days it can be hidden for 3 days; from Nov 9 it counts down and stays.
 * Built on the app's surface tokens with a faint brand edge, like the trial
 * bar, rather than a slab of colour that reads as an alert and glares in dark
 * mode; the one primary element is the button.
 */
export function AppSumoUpgradeBanner() {
  const posthog = usePostHog();
  const { user } = useUser();
  const userId = user?.id;
  const { offer, prices, active, phase } = useAppSumoUpgrade();
  const leadId = useId();
  // The plan wizard shows the member price, so the bar steps aside there
  // rather than link to the page it is on.
  const { pathname } = useLocation();
  const onUpgradePage =
    pathname === '/upgrade' || pathname.startsWith(APPSUMO_UPGRADE_PATH);

  const [dismissed, setDismissed] = useState(() => readDismissed(userId));
  useEffect(() => {
    setDismissed(readDismissed(userId));
  }, [userId]);

  // "Tomorrow" and "tonight" turn over at midnight; a minute's tick keeps a
  // tab left open overnight honest without re-rendering every second.
  const finalDays = active && phase === 'final_week';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!finalDays) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [finalDays]);

  // Worded only for an offer that is up (the hook vouches for its deadline
  // and both prices).
  const copy =
    active && offer && prices && phase
      ? appSumoBannerCopy(prices, offer.ends_at, phase, now)
      : null;
  const visible =
    active && !onUpgradePage && !!copy && !(copy.dismissible && dismissed);

  // Once per phase a bar is shown in, not on every refetch of the offer.
  const viewedPhase = useRef<string | null>(null);
  useEffect(() => {
    if (!visible || !offer || !phase || viewedPhase.current === phase) return;
    viewedPhase.current = phase;
    trackAppSumoUpgrade(posthog, 'appsumo_upgrade_banner_viewed', {
      tier: offer.appsumo_tier,
      phase,
      members: offer.members,
    });
  }, [visible, offer, phase, posthog]);

  if (!visible || !offer || !phase || !copy) return null;

  const dismiss = () => {
    if (userId) writeDismissed(userId);
    setDismissed(true);
    trackAppSumoUpgrade(posthog, 'appsumo_upgrade_banner_dismissed', {
      tier: offer.appsumo_tier,
    });
  };

  return (
    <div
      role='region'
      aria-labelledby={leadId}
      className={cn(
        'relative flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2',
        'border-b border-border bg-card px-4 py-2 text-[13px] sm:flex-nowrap',
        copy.dismissible && 'pr-11',
        // A whisper of brand along the edges, as on the trial bar: ours,
        // without becoming a coloured slab. Stronger in dark, where 5% on a
        // near-black surface disappears.
        'bg-[linear-gradient(90deg,hsl(var(--primary)/0.05)_0%,transparent_45%,hsl(var(--primary)/0.05)_100%)]',
        'dark:bg-[linear-gradient(90deg,hsl(var(--primary)/0.11)_0%,transparent_45%,hsl(var(--primary)/0.11)_100%)]'
      )}
    >
      <p className='min-w-0 text-center text-balance text-foreground sm:text-left'>
        <span id={leadId} className='font-semibold'>
          {copy.lead}
        </span>
        <span className='hidden text-muted-foreground md:inline'>
          {' '}
          {copy.rest}
        </span>
      </p>

      <Link
        to='/upgrade'
        search={appSumoUpgradeSearch({ source: 'banner' })}
        onClick={() =>
          trackAppSumoUpgrade(posthog, 'appsumo_upgrade_banner_clicked', {
            tier: offer.appsumo_tier,
            phase,
          })
        }
        className={cn(
          PRIMARY_BUTTON,
          'inline-flex h-7 shrink-0 items-center gap-1.5 px-3 whitespace-nowrap',
          'text-xs font-semibold',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden'
        )}
      >
        {SEE_YOUR_PRICE}
        <AppIcon icon={iconArrowRight} aria-hidden style={{ fontSize: 10 }} />
      </Link>

      {copy.dismissible && (
        <button
          type='button'
          aria-label={BANNER_HIDE_LABEL}
          onClick={dismiss}
          className='absolute top-1/2 right-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden'
        >
          <AppIcon icon={iconXmark} style={{ fontSize: 12 }} />
        </button>
      )}
    </div>
  );
}
